﻿import { useEffect, useMemo, useState } from 'react';
import { Box, DataTable } from 'grommet';
import { Configure } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { ProgramBrokerFeeRateService } from '/src/api/services/ProgramBrokerFeeRateService';
import { ProgramRecCostService } from '/src/api/services/ProgramRecCostService';
import { ProgramService } from '/src/api/services/ProgramService';
import { FooterNav, HeaderNav } from '/src/components';
import AdminSideNav from '/src/components/admin/AdminSideNav';
import {
  Card,
  CardBody,
  CardHeader,
  DataTableHeader,
  DataTableItem,
  LoadingSpinner,
  Text,
} from '/src/components/common';
import { useDashboardStore } from '/src/context';
import { Program } from '/src/lib/models/Program';
import { ProgramBrokerFeeRate } from '/src/lib/models/ProgramBrokerFeeRate';
import { ProgramRecCost } from '/src/lib/models/ProgramRecCost';
import { getQuarterFromDateString } from '/src/utils/formatting';

const FuseAdminCredit = observer(() => {
  const dashboardStore = useDashboardStore();
  const reportingPeriods = dashboardStore.dashboardData?.reporting_periods || [];
  const [currentQuarterIndex, setCurrentQuarterIndex] = useState(0);
  const [brokerFeeRates, setBrokerFeeRates] = useState<ProgramBrokerFeeRate[]>([]);
  const [recCosts, setRecCosts] = useState<ProgramRecCost[]>([]);
  const [allPrograms, setAllPrograms] = useState<Program[]>([]);
  const [isLoadingPrograms, setIsLoadingPrograms] = useState(true);
  const [isLoadingBrokerFees, setIsLoadingBrokerFees] = useState(true);
  const [isLoadingRecCosts, setIsLoadingRecCosts] = useState(true);
  const [isDashboardLoading, setIsDashboardLoading] = useState(true);

  const quarters = useMemo(() => {
    const quarterStrings = reportingPeriods.map((p) => p.reporting_period);

    return quarterStrings.sort((a, b) => {
      const parseQuarter = (quarterStr: string) => {
        const match = quarterStr.match(/Q(\d+)\s+(\d+)/);
        if (!match) return { quarter: 0, year: 0 };
        return { quarter: parseInt(match[1], 10), year: parseInt(match[2], 10) };
      };

      const aParsed = parseQuarter(a);
      const bParsed = parseQuarter(b);

      if (bParsed.year !== aParsed.year) {
        return bParsed.year - aParsed.year;
      }
      return bParsed.quarter - aParsed.quarter;
    });
  }, [reportingPeriods]);

  useEffect(() => {
    setIsDashboardLoading(true);
    const fetchDashboard = async () => {
      try {
        await dashboardStore.getAdminView();
      } finally {
        setIsDashboardLoading(false);
      }
    };

    fetchDashboard();
    return () => dashboardStore.clearDashboardData();
  }, []);

  useEffect(() => {
    setIsLoadingPrograms(true);
    setIsLoadingBrokerFees(true);
    setIsLoadingRecCosts(true);

    ProgramRecCostService.list()
      .then(setRecCosts)
      .finally(() => setIsLoadingRecCosts(false));

    ProgramBrokerFeeRateService.list()
      .then(setBrokerFeeRates)
      .finally(() => setIsLoadingBrokerFees(false));

    ProgramService.list()
      .then(setAllPrograms)
      .finally(() => setIsLoadingPrograms(false));
  }, []);

  useEffect(() => {
    if (quarters.length > 0 && currentQuarterIndex >= quarters.length) {
      setCurrentQuarterIndex(0);
    }
  }, [quarters.length, currentQuarterIndex]);

  const currentQuarter = quarters[currentQuarterIndex] || '';

  const getMatchingRate = (programId: number) => {
    const brokerFeeRate = brokerFeeRates.find(
      (rate) =>
        rate.program_id === programId && getQuarterFromDateString(rate.start_reporting_quarter) === currentQuarter
    );
    const recCost = recCosts.find(
      (cost) =>
        cost.program_id === programId && getQuarterFromDateString(cost.start_reporting_quarter) === currentQuarter
    );
    return {
      brokerFeeRate: brokerFeeRate ?? undefined,
      recCost: recCost ?? undefined,
    };
  };

  const getMatchingProgram = (programName: string) => {
    const period = reportingPeriods.find((p) => p.reporting_period === currentQuarter);
    return period?.programs.find((p) => p.name === programName);
  };

  type ProgramPricingAndFees = {
    program_id: number;
    program_name: string;
    price: string;
    broker_fee: string;
    rec_fee: string;
    quarter: string;
  };

  const combinedProgramData: ProgramPricingAndFees[] = useMemo(
    () =>
      allPrograms.map((program) => {
        const matchingRate = getMatchingRate(program.id);
        const matchingProgram = getMatchingProgram(program.name);
        const formattedPercentRate = matchingRate.brokerFeeRate ? `${matchingRate.brokerFeeRate.percent_rate}%` : '—';

        return {
          program_id: program.id,
          program_name: program.name,
          price: matchingProgram?.price_per_credit ?? '—',
          broker_fee: formattedPercentRate,
          rec_fee: matchingRate.recCost ? `${matchingRate.recCost.cost}` : '—',
          quarter: currentQuarter || '—',
        };
      }),
    [allPrograms, brokerFeeRates, recCosts, reportingPeriods, currentQuarter]
  );

  const isPricingAndFeesLoading = isLoadingPrograms || isLoadingBrokerFees || isLoadingRecCosts || isDashboardLoading;

  // Navigation
  const handlePrev = () => {
    setCurrentQuarterIndex((i) => Math.min(i + 1, quarters.length - 1));
  };

  const handleNext = () => {
    setCurrentQuarterIndex((i) => Math.max(i - 1, 0));
  };

  return (
    <Box height={{ min: '100vh' }} background="light" style={{ display: 'flex', flexDirection: 'column' }}>
      <HeaderNav />
      <Box flex="grow" style={{ display: 'flex', flexDirection: 'row', minHeight: 0 }}>
        <AdminSideNav />
        <Box flex="grow" pad={{ horizontal: 'large', vertical: 'medium' }} overflow="auto">
          <Box flex={false}>
            <Box
              direction="row"
              align="center"
              justify="between"
              margin={{ vertical: 'medium' }}
              pad={{ horizontal: 'small' }}
            >
              <Text size="xlarge" weight="normal">
                Credit Pricing & Fees
              </Text>
            </Box>
            <Box
              direction="row"
              border={{ color: 'light-4', size: '1px' }}
              round="medium"
              width="160px"
              align="center"
              justify="between"
              pad={{ horizontal: 'small', vertical: 'xsmall' }}
              background="light-4"
            >
              <Box
                onClick={currentQuarterIndex < quarters.length - 1 ? handlePrev : undefined}
                pad="xsmall"
                style={{
                  cursor: currentQuarterIndex < quarters.length - 1 ? 'pointer' : 'not-allowed',
                  opacity: currentQuarterIndex < quarters.length - 1 ? 1 : 0.5,
                }}
              >
                <Text size="small">←</Text>
              </Box>
              <Text size="small" weight="bold">
                {currentQuarter}
              </Text>
              <Box
                onClick={currentQuarterIndex > 0 ? handleNext : undefined}
                pad="xsmall"
                style={{
                  cursor: currentQuarterIndex > 0 ? 'pointer' : 'not-allowed',
                  opacity: currentQuarterIndex > 0 ? 1 : 0.5,
                }}
              >
                <Text size="small">→</Text>
              </Box>
            </Box>

            <Card margin={{ vertical: 'large' }} background="white" elevation="none" round="xsmall">
              <CardHeader
                pad={{ horizontal: 'medium', vertical: 'medium' }}
                border={{ side: 'bottom', color: 'light-4', size: '1px' }}
              >
                <Text size="large" weight="normal">
                  Credit Pricing, Broker Fees, and REC Fees
                </Text>
              </CardHeader>
              <CardBody pad="none">
                <Box elevation="small">
                  {isPricingAndFeesLoading ? (
                    <Box height="200px">
                      <LoadingSpinner />
                    </Box>
                  ) : (
                    <DataTable
                      pad={{ horizontal: '1.5rem' }}
                      columns={[
                        {
                          property: 'program_name',
                          primary: true,
                          size: '25%',
                          align: 'start',
                          verticalAlign: 'top',
                          header: <DataTableHeader toUpperCase>PROGRAM NAME</DataTableHeader>,
                          render: (datum: ProgramPricingAndFees) => (
                            <Box pad={{ vertical: '1rem' }}>
                              <DataTableItem icon={<Configure size="small" color="brand" />}>
                                {datum.program_name}
                              </DataTableItem>
                            </Box>
                          ),
                        },
                        {
                          property: 'price',
                          size: '20%',
                          align: 'start',
                          verticalAlign: 'top',
                          header: <DataTableHeader toUpperCase>CREDIT PRICE</DataTableHeader>,
                          render: (datum: ProgramPricingAndFees) => (
                            <Box pad={{ vertical: '1rem' }}>
                              <DataTableItem>{datum.price === '—' ? datum.price : `$${datum.price}`}</DataTableItem>
                            </Box>
                          ),
                        },
                        {
                          property: 'rec_fee',
                          size: '20%',
                          align: 'start',
                          verticalAlign: 'top',
                          header: <DataTableHeader toUpperCase>REC Price</DataTableHeader>,
                          render: (datum: ProgramPricingAndFees) => (
                            <Box pad={{ vertical: '1rem' }}>
                              <DataTableItem>
                                {datum.rec_fee === '—' ? datum.rec_fee : `$${datum.rec_fee}`}
                              </DataTableItem>
                            </Box>
                          ),
                        },
                        {
                          property: 'broker_fee',
                          size: '20%',
                          align: 'start',
                          verticalAlign: 'top',
                          header: (
                            <DataTableHeader toUpperCase subText="(Percent Rate)">
                              BROKER FEE
                            </DataTableHeader>
                          ),
                          render: (datum: ProgramPricingAndFees) => (
                            <Box pad={{ vertical: '1rem' }}>
                              <DataTableItem>{datum.broker_fee}</DataTableItem>
                            </Box>
                          ),
                        },

                        {
                          property: 'quarter',
                          size: '15%',
                          align: 'start',
                          verticalAlign: 'top',
                          header: <DataTableHeader toUpperCase>QUARTER</DataTableHeader>,
                          render: (datum: ProgramPricingAndFees) => (
                            <Box pad={{ vertical: '1rem' }}>
                              <DataTableItem>{datum.quarter}</DataTableItem>
                            </Box>
                          ),
                        },
                      ]}
                      data={combinedProgramData}
                      background={['light-6', 'white']}
                      border={{ color: 'light-2', side: 'bottom', size: 'small' }}
                    />
                  )}
                </Box>
              </CardBody>
            </Card>
          </Box>
        </Box>
      </Box>
      <Box flex={false}>
        <FooterNav />
      </Box>
    </Box>
  );
});

export default FuseAdminCredit;
